import React, { useMemo, useState } from 'react';
import { FileCode2, RefreshCw, Save, ChevronDown, ChevronRight } from 'lucide-react';
import { toCurrency } from '../utils/adminMetrics';

// One NAMFISA form shown the way it goes into the return's XML: the XML header,
// then the form's XML part with one row per element — its NAMFISA name, the
// field it comes from, the value (editable), the system's suggestion, and the
// exact value that will be written. Fields not placed in the XML yet are listed
// under it, still editable, so nothing entered is lost.
//
// layout  — GET /api/namfisa/reports/:y/:q/xml-view/:return
// section — the report section (fields, values, suggestedValues)

const isEmpty = (v) => v === null || v === undefined || v === '';

const xmlValue = (el, raw) => {
  if (isEmpty(raw)) return null;
  if (el.kind === 'decimal') {
    const n = Number(raw);
    return Number.isFinite(n) ? n.toFixed(el.decimals || 0) : null;
  }
  const s = String(raw).trim();
  if (!s) return null;
  return el.maxLength ? s.slice(0, el.maxLength) : s;
};

const numberish = (field) => field && field.type !== 'text';

const inputCls = 'w-full';

const NamfisaXmlView = ({ layout, section, onChange, onSave, onRecompute, recomputing }) => {
  const [showUnmapped, setShowUnmapped] = useState(false);
  const placement = layout?.sections?.[section.section] || { path: [], fields: [], cells: [] };
  const fieldsByKey = useMemo(() => {
    const map = {};
    for (const f of section.fields) {
      map[f.key] = f;
      if (f.specifyKey) map[f.specifyKey] = { key: f.specifyKey, label: `${f.label} — please specify`, type: 'text', group: f.group };
    }
    return map;
  }, [section.fields]);

  const values = section.values || {};
  const suggested = section.suggestedValues || {};

  // Rows for single fields and for grid cells, in XML order.
  const rows = useMemo(() => {
    const out = placement.fields.map((el) => {
      const f = fieldsByKey[el.key] || { key: el.key, label: el.key, type: el.kind === 'decimal' ? 'number' : 'text' };
      return {
        id: el.key, el, field: f, label: f.label, group: f.group,
        value: values[el.key], suggestion: suggested[el.key],
        set: (v) => onChange(el.key, v),
      };
    });
    for (const el of placement.cells) {
      const f = fieldsByKey[el.key] || {};
      const rowLabel = (f.rows || []).find((r) => r.key === el.row)?.label || el.row;
      const colLabel = (f.columns || []).find((c) => c.key === el.col)?.label || el.col;
      const matrix = values[el.key] || {};
      out.push({
        id: `${el.key}.${el.row}.${el.col}`, el, field: { ...f, type: 'number' },
        label: `${rowLabel} · ${colLabel}`, group: f.group,
        value: matrix?.[el.row]?.[el.col], suggestion: suggested[el.key]?.[el.row]?.[el.col],
        set: (v) => onChange(el.key, { ...matrix, [el.row]: { ...(matrix[el.row] || {}), [el.col]: v } }),
      });
    }
    return out;
  }, [placement, fieldsByKey, values, suggested, onChange]);

  // Fields (and grid cells) of this form that are not written to the XML yet.
  const unmapped = useMemo(() => {
    const mappedFields = new Set(placement.fields.map((e) => e.key));
    const mappedCells = new Set(placement.cells.map((e) => `${e.key}.${e.row}.${e.col}`));
    const out = [];
    for (const f of section.fields) {
      if (f.displayOnly) continue; // shown on its own, to type into the portal
      if (f.type === 'matrix') {
        for (const r of f.rows || []) for (const c of f.columns || []) {
          if (mappedCells.has(`${f.key}.${r.key}.${c.key}`)) continue;
          const matrix = values[f.key] || {};
          out.push({
            id: `${f.key}.${r.key}.${c.key}`, field: { ...f, type: 'number' }, label: `${r.label} · ${c.label}`, group: f.group,
            value: matrix?.[r.key]?.[c.key], suggestion: suggested[f.key]?.[r.key]?.[c.key],
            set: (v) => onChange(f.key, { ...matrix, [r.key]: { ...(matrix[r.key] || {}), [c.key]: v } }),
          });
        }
        continue;
      }
      for (const key of [f.key, f.specifyKey].filter(Boolean)) {
        if (mappedFields.has(key)) continue;
        const def = fieldsByKey[key];
        out.push({ id: key, field: def, label: def.label, group: f.group, value: values[key], suggestion: suggested[key], set: (v) => onChange(key, v) });
      }
    }
    return out;
  }, [section.fields, placement, fieldsByKey, values, suggested, onChange]);

  // Figures the portal asks for but NAMFISA's XML has no element for.
  const displayOnly = section.fields.filter((f) => f.displayOnly);

  const hasComputed = section.fields.some((f) => f.computed);
  const written = rows.filter((r) => xmlValue(r.el, isEmpty(r.value) ? r.suggestion : r.value) !== null).length;

  const valueInput = (r) => (
    <input
      type={numberish(r.field) ? 'number' : 'text'}
      step={numberish(r.field) ? 'any' : undefined}
      value={isEmpty(r.value) ? '' : r.value}
      placeholder={isEmpty(r.suggestion) ? '' : String(r.suggestion)}
      onChange={(e) => r.set(e.target.value === '' ? null : (numberish(r.field) ? e.target.value : e.target.value))}
      className={inputCls}
      aria-label={r.label}
    />
  );
  const suggestionCell = (r) => (isEmpty(r.suggestion) ? (
    <span className="text-gray-300 dark:text-slate-600">—</span>
  ) : (
    <span className="inline-flex items-center gap-1.5">
      <span className="tabular-nums">{String(r.suggestion)}</span>
      {String(r.suggestion) !== String(r.value ?? '') && (
        <button type="button" onClick={() => r.set(r.suggestion)}
          className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300">
          Use
        </button>
      )}
    </span>
  ));

  return (
    <div className="space-y-3">
      {/* The return's header, as written at the top of the XML. */}
      <div className="rounded-lg border border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm">
        <div className="px-4 py-2 border-b border-gray-100 dark:border-slate-700 flex items-center gap-2">
          <FileCode2 size={15} className="text-blue-600 dark:text-blue-400" />
          <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100">Quarterly Return {layout?.code} — XML header</h3>
          <span className="ml-auto text-[11px] text-gray-400 dark:text-slate-500">from Company Configuration and the quarter</span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-4 py-3">
          {(layout?.header || []).map((h) => (
            <div key={h.name} className="min-w-0">
              <div className="text-[10px] font-mono text-gray-400 dark:text-slate-500">&lt;{h.name}&gt;</div>
              <div className="text-xs font-semibold text-gray-900 dark:text-slate-100 truncate" title={h.value}>{h.value || '—'}</div>
            </div>
          ))}
        </div>
      </div>

      {displayOnly.length > 0 && (
        <div className="rounded-lg border border-amber-200 dark:border-amber-800/50 bg-amber-50/60 dark:bg-amber-900/10 px-4 py-3 space-y-2">
          {displayOnly.map((f) => {
            const v = isEmpty(values[f.key]) ? suggested[f.key] : values[f.key];
            return (
              <div key={f.key} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="text-sm font-semibold text-gray-800 dark:text-slate-100">{f.label}</span>
                <span className="text-sm font-mono tabular-nums text-gray-900 dark:text-slate-100">
                  {isEmpty(v) ? '—' : toCurrency(v)}
                </span>
                <span className="rounded-full border border-amber-300 dark:border-amber-700 bg-white dark:bg-slate-800 px-2 py-0.5 text-[10px] font-semibold text-amber-800 dark:text-amber-300">Type into the portal</span>
                {f.computedNote && <div className="w-full text-[11px] text-gray-500 dark:text-slate-400">{f.computedNote}</div>}
              </div>
            );
          })}
        </div>
      )}

      {/* The form's XML part. */}
      <div className="rounded-lg border border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm">
        <div className="px-4 py-3 border-b border-gray-100 dark:border-slate-700 flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-blue-800 dark:text-blue-300">Part {section.part} · {section.title}</h2>
            <p className="mt-0.5 text-[11px] font-mono text-gray-500 dark:text-slate-400 truncate">
              {layout?.inner} › {placement.path.join(' › ')}
            </p>
            <p className="mt-0.5 text-[11px] text-gray-500 dark:text-slate-400">
              {written} of {rows.length} elements will be written. An empty value uses the suggestion; with neither, the element is left out.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {hasComputed && (
              <button type="button" onClick={onRecompute} disabled={recomputing}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700/50 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 disabled:opacity-50"
                title="Refresh suggested values from current loan and repayment data">
                <RefreshCw size={13} className={recomputing ? 'animate-spin' : ''} /> Recompute from data
              </button>
            )}
            <button type="button" onClick={onSave}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700">
              <Save size={13} /> Save section
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-blue-50 dark:bg-blue-900/20 text-left text-[10px] uppercase tracking-wide text-blue-900 dark:text-blue-200">
              <tr>
                <th className="px-3 py-2">XML element</th>
                <th className="px-3 py-2">Field</th>
                <th className="px-3 py-2 w-40">Value</th>
                <th className="px-3 py-2">Suggested</th>
                <th className="px-3 py-2 text-right">Written to XML</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
              {rows.map((r) => {
                const out = xmlValue(r.el, isEmpty(r.value) ? r.suggestion : r.value);
                return (
                  <tr key={r.id} className="align-middle">
                    <td className="px-3 py-1.5 font-mono text-[11px] text-gray-700 dark:text-slate-300 whitespace-nowrap">&lt;{r.el.name}&gt;</td>
                    <td className="px-3 py-1.5 text-gray-800 dark:text-slate-200">
                      {r.label}
                      {r.group && <div className="text-[10px] text-gray-400 dark:text-slate-500">{r.group}</div>}
                    </td>
                    <td className="px-3 py-1.5">{valueInput(r)}</td>
                    <td className="px-3 py-1.5 text-gray-600 dark:text-slate-300">{suggestionCell(r)}</td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                      {out === null
                        ? <span className="text-gray-300 dark:text-slate-600">left out</span>
                        : <span className="text-emerald-700 dark:text-emerald-400">{out}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {unmapped.length > 0 && (
          <div className="border-t border-gray-100 dark:border-slate-700">
            <button type="button" onClick={() => setShowUnmapped((v) => !v)}
              className="w-full px-4 py-2 flex items-center gap-1.5 text-xs font-semibold text-amber-700 dark:text-amber-300 hover:bg-amber-50/60 dark:hover:bg-amber-900/10">
              {showUnmapped ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
              Not in the XML yet ({unmapped.length})
              <span className="font-normal text-gray-500 dark:text-slate-400">— kept and saved, but not written until their NAMFISA element is confirmed</span>
            </button>
            {showUnmapped && (
              <table className="min-w-full text-xs">
                <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                  {unmapped.map((r) => (
                    <tr key={r.id}>
                      <td className="px-3 py-1.5 text-gray-800 dark:text-slate-200">
                        {r.label}
                        {r.group && <div className="text-[10px] text-gray-400 dark:text-slate-500">{r.group}</div>}
                      </td>
                      <td className="px-3 py-1.5 w-40">{valueInput(r)}</td>
                      <td className="px-3 py-1.5 text-gray-600 dark:text-slate-300">{suggestionCell(r)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NamfisaXmlView;
